import netflixLibrary from '../dict/netflixGlyphs.js';
import sccLibrary from '../dict/608.js';
import convertToPlainText from '../functions/quill/convertToPlainText.js';
import getLineCount from '../functions/quill/getLineCount.js';
import tcLib from './timecode.js';
import { decode } from 'html-entities';

/**
 * Calculates the duration between start and end times with specified precision
 * @param {number} start - Start time in seconds
 * @param {number} end - End time in seconds
 * @param {number} [precision=3] - Number of decimal places for result
 * @returns {number} Duration in seconds with specified precision
 */
export function calculateDuration(start, end, precision = 3) {
  start = parseFloat(start);
  end = parseFloat(end);
  if (isNaN(start) || isNaN(end)) {
    return 0;
  }
  return parseFloat((end - start).toFixed(precision));
}

/**
 * Tests if event text exceeds maximum line count
 * @param {string} html - HTML content of the event
 * @param {number} maxLines - Maximum allowed lines
 * @returns {string|undefined} Error message or undefined if test passes
 */
export function eventLineTest(html, maxLines) {
  if (convertToPlainText(html).trim().length === 0) {
    return "Event text is empty";
  }
  if (convertToPlainText(html).trim() === "<br>") {
    return "Event text is empty";
  }
  let lineCount = getLineCount(html);
  if (lineCount > maxLines) {
    return `Line count (${lineCount}) exceeds max line count of ${maxLines}`;
  } else {
    return;
  }
}

/**
 * Tests if total character count exceeds maximum
 * @param {string} html - HTML content of the event
 * @param {number} totalMaxChars - Maximum allowed total characters
 * @returns {string|undefined} Error message or undefined if test passes
 */
export function totalCharTest(html, totalMaxChars) {
  if (!totalMaxChars) {
    return;
  }
  let plainText = convertToPlainText(html, "");
  let failed = false;
  if (plainText.trim().length === 0) {
    return "Event text is empty";
  }
  if (plainText.length > totalMaxChars) {
    failed = true;
  }
  if (failed) {
    return "Maximum character count of line is higher than " + totalMaxChars;
  } else {
    return;
  }
}

/**
 * Tests if any line exceeds maximum character count
 * @param {string} html - HTML content of the event
 * @param {number} maxChars - Maximum allowed characters per line
 * @returns {string|undefined} Error message or undefined if test passes
 */
export function eventCharTest(html, maxChars) {
  let plainText = convertToPlainText(html);
  let failed = false;
  if (plainText.trim().length === 0) {
    return "Event text is empty";
  }
  plainText.split("\n").forEach(line => {
    if (line.length > maxChars) {
      failed = true;
    }
  });
  if (failed) {
    return "Maximum character count of line is higher than " + maxChars;
  } else {
    return;
  }
}

/**
 * Tests if event duration is within allowed range
 * @param {number} start - Start time in seconds
 * @param {number} end - End time in seconds
 * @param {number} min - Minimum allowed duration
 * @param {number} max - Maximum allowed duration
 * @returns {string|undefined} Error message or undefined if test passes
 */
export function eventDurationTest(start, end, min, max) {
  if (isNaN(start)) {
    return "Start time is not set";
  }
  if (isNaN(end)) {
    return "End time is not set";
  }
  start = parseFloat(start);
  end = parseFloat(end);
  let duration = calculateDuration(start, end, 3);
  if (isNaN(duration)) {
    return "Start or end time is not set";
  }
  if (duration > parseFloat(max)) {
    return `Event duration (${duration.toFixed(3)}) is greater than ${parseFloat(max)} seconds`;
  } else if (duration < parseFloat(min)) {
    return `Event duration (${duration.toFixed(3)}) is less than ${parseFloat(min)} seconds`;
  } else {
    return;
  }
}

/**
 * Extracts and counts the total number of characters from HTML content
 * @param {string} html - HTML content to process
 * @param {string} [type="html"] - Whether the input is "html" or plain "text"
 * @param {boolean} [excludeSpaces=false] - When true, spaces are excluded from the count (used for CPS reading rate)
 * @returns {number} Total character count
 */
export function getTotalChars(html, type = "html", excludeSpaces = false) {
  if (!html) {
    return 0;
  }
  let plainText = type === "html" ? convertToPlainText(html) : html;
  if (plainText.trim().length === 0) {
    return 0;
  }
  let lineInfo = [];
  plainText.split("\n").forEach(line => {
    let content = decode(line);
    if (excludeSpaces) {
      content = content.replace(/ /g, "");
    }
    lineInfo.push(content.length);
  });
  return lineInfo.reduce((partialSum, a) => partialSum + a, 0);
}

/**
 * Calculates the characters per second rate
 * @param {number} totalChars - Total character count
 * @param {number} duration - Duration in seconds
 * @param {number} [precision=3] - Number of decimal places for result
 * @returns {number} Characters per second rate
 */
export function calculateCps(totalChars, duration, precision = 3) {
  if (duration <= 0 || totalChars <= 0) {
    return 0;
  }
  return parseFloat((totalChars / duration).toFixed(precision));
}

/**
 * Tests if characters per second are within allowed range
 * @param {number} start - Start time in seconds
 * @param {number} end - End time in seconds
 * @param {string} html - HTML content of the event
 * @param {number} min - Minimum allowed CPS
 * @param {number} max - Maximum allowed CPS
 * @param {boolean} [excludeSpaces=false] - When true, spaces are excluded from the CPS character count
 * @returns {string|undefined} Error message or undefined if test passes
 */
export function eventCpsTest(start, end, html, min, max, excludeSpaces = false) {
  if (isNaN(start)) {
    return "Start time is not set";
  }
  if (isNaN(end)) {
    return "End time is not set";
  }
  start = parseFloat(start);
  end = parseFloat(end);
  let duration = calculateDuration(start, end, 3);
  let plainText = convertToPlainText(html);
  if (plainText.trim().length === 0) {
    return "Event text is empty";
  }

  // Use the new functions instead of inline logic
  const totalChars = getTotalChars(html, "html", excludeSpaces);
  const cps = calculateCps(totalChars, duration);
  if (cps > max) {
    return `Reading rate (${cps}) is greater than ${max}`;
  } else if (cps < min) {
    return `Reading rate (${cps}) is less than ${min}`;
  } else {
    return;
  }
}

/**
 * Counts the total number of words in HTML content
 * @param {string} html - HTML content to process
 * @returns {number} Total word count
 */
export function getTotalWords(html) {
  if (!html) {
    return 0;
  }
  const plainText = convertToPlainText(html);
  if (plainText.trim().length === 0) {
    return 0;
  }
  return plainText.split(/\s/gm).length;
}

/**
 * Calculates the words per minute rate
 * @param {number} totalWords - Total word count
 * @param {number} duration - Duration in seconds
 * @param {number} [precision=3] - Number of decimal places for result
 * @returns {number} Words per minute rate
 */
export function calculateWpm(totalWords, duration, precision = 3) {
  if (duration <= 0 || totalWords <= 0) {
    return 0;
  }
  return parseFloat((totalWords / duration * 60).toFixed(precision));
}

/**
 * Tests if words per minute are within allowed range
 * @param {number} start - Start time in seconds
 * @param {number} end - End time in seconds
 * @param {string} html - HTML content of the event
 * @param {number} min - Minimum allowed WPM
 * @param {number} max - Maximum allowed WPM
 * @returns {string|undefined} Error message or undefined if test passes
 */
export function eventWpmTest(start, end, html, min, max) {
  if (isNaN(start)) {
    return "Start time is not set";
  }
  if (isNaN(end)) {
    return "End time is not set";
  }
  start = parseFloat(start);
  end = parseFloat(end);
  let duration = calculateDuration(start, end, 3);
  let plainText = convertToPlainText(html);
  if (plainText.trim().length === 0) {
    return "Event text is empty";
  }

  // Use the new functions instead of inline logic
  const totalWords = getTotalWords(html);
  const wpm = calculateWpm(totalWords, duration);
  if (wpm > max || wpm < min) {
    return `Words per minute is ${wpm}. Min and max WPM is set to ${min}/${max}`;
  } else {
    return;
  }
}

/**
 * Tests if gap between events is within allowed range
 * @param {number} eventAEnd - End time of first event
 * @param {number} eventBStart - Start time of next event
 * @param {number} min - Minimum allowed gap in frames
 * @param {number} max - Maximum allowed gap in frames
 * @param {number} minTolerance - Minimum tolerance allowed in frames
 * @param {number} maxTolerance - Maximum tolerance allowed in frames
 * @param {number} frameRate - Project frame rate
 * @returns {string|undefined} Error message or undefined if test passes
 */
export function eventGapTest(eventAEnd, eventBStart, min, max, minTolerance, maxTolerance, frameRate) {
  if (isNaN(eventAEnd)) {
    return "Failed Gap Test: End Timecode is not set";
  }
  if (isNaN(eventBStart)) {
    return "Failed Gap Test: Start timecode of next event is not set";
  }
  let tcStart = tcLib.secToFrames(eventBStart, frameRate);
  let tcEnd = tcLib.secToFrames(eventAEnd, frameRate);
  minTolerance = isNaN(minTolerance) ? min : parseInt(minTolerance);
  maxTolerance = isNaN(maxTolerance) ? 0 : parseInt(maxTolerance);
  let gap = tcStart - tcEnd;
  if (gap > parseInt(max)) {
    return `The frame gap (${gap}) is larger than ${parseInt(max)} frames`;
  } else if (parseInt(gap) < min && parseInt(gap) <= minTolerance) {
    return `The frame gap (${gap}) is less then ${parseInt(min)} frames`;
  } else if (parseInt(gap) > min && parseInt(gap) <= maxTolerance) {
    return `The frame gap (${gap}) is within the tolerance range of ${minTolerance}-${maxTolerance} frames`;
  } else {
    return;
  }
}

/**
 * Tests if events overlap
 * @param {number} eventAEnd - End time of first event
 * @param {number} eventBStart - Start time of next event
 * @returns {string|undefined} Error message or undefined if test passes
 */
export function overlapTest(eventAEnd, eventBStart) {
  if (isNaN(eventAEnd)) {
    return "Failed Overlap Test: End Timecode is not set";
  }
  if (isNaN(eventBStart)) {
    return "Failed Overlap Test: Start timecode of next event is not set";
  }
  if (eventBStart < eventAEnd) {
    return "Failed Overlap Test: Event overlap found";
  }
  return;
}

/**
 * Tests for illegal characters
 * @param {string} html - HTML content of the event
 * @param {Array} lib - Library of valid characters
 * @returns {string|undefined} Error message or undefined if test passes
 */
export function detectIllegalChars(html, lib) {
  let text = convertToPlainText(html, "");
  let illegalChars = text.split("").find(char => {
    if (char.charCodeAt(0) === 32) {
      return;
    }
    return lib.indexOf(char) === -1;
  });
  if (illegalChars && illegalChars.length > 0) {
    return `Event contains illegal characters (${illegalChars[0]})`;
  }
  return;
}

/**
 * Tests if event has specific position
 * @param {Object} event - Event object
 * @param {string} y - Y position (start, center, end)
 * @param {string} x - X position (start, center, end)
 * @param {string} positionName - Human-readable position name
 * @returns {string|undefined} Error message or undefined if test passes
 */
export function positionTest(event, y, x, positionName) {
  if (event.xPos === x && event.yPos === y) {
    return `Event is positioned ${positionName}`;
  } else {
    return;
  }
}

/**
 * Tests if subtitle text could fit on one line
 * @param {string} html - HTML content of the event
 * @param {number} maxChars - Maximum allowed characters per line
 * @returns {string|undefined} Error message or undefined if test passes
 */
export function checkSubtitleFit(html, maxChars) {
  let totalChars = 0;
  let plainText = convertToPlainText(html);
  let textLines = plainText.split("\n");
  if (textLines.length === 1) {
    return;
  }
  textLines.forEach(line => {
    totalChars += line.trim().length;
  });
  if (totalChars <= maxChars) {
    return "Event text can fit on one line";
  }
  return;
}

/**
 * Tests for leading or trailing spaces
 * @param {string} html - HTML content of the event
 * @returns {string|undefined} Error message or undefined if test passes
 */
export function checkLeadingSpaces(html) {
  //replace all tab characters:
  html = html.replace(/\t/g, "");
  if (html.match(/<p[^>]*>\s|\s<\/p>/)) {
    return "Leading or trailing spaces found";
  }
  return;
}

/**
 * Tests for multiple whitespace characters
 * @param {string} html - HTML content of the event
 * @returns {string|undefined} Error message or undefined if test passes
 */
export function checkWhiteSpace(html) {
  if (html.match(/\s{2,}/)) {
    return "Leading or trailing spaces found";
  }
  return;
}

/**
 * Tests for empty lines
 * @param {string} html - HTML content of the event
 * @returns {string|undefined} Error message or undefined if test passes
 */
export function checkBlankLines(html) {
  if (html.match(/><br><\/p>/)) {
    return "Event text contains empty lines";
  }
}

/**
 * Tests for text formatting
 * @param {string} htmlString - HTML content of the event
 * @param {string} specificTag - HTML tag to check (em, strong, u)
 * @param {boolean} full - Whether to check for full or partial formatting
 * @returns {boolean} Whether the formatting exists
 */
export function checkForFormatting(htmlString, specificTag, full = true) {
  try {
    const tagRegex = new RegExp(`<${specificTag}[^>]*>.*?</${specificTag}>`, 'i');
    if (!tagRegex.test(htmlString)) {
      return false;
    }
    let cleaned = htmlString.replace(new RegExp(`<(?!/?${specificTag})[^>]+>`, 'g'), '');

    // Remove the specific tag and its content
    let withoutSpecific = cleaned.replace(new RegExp(`<${specificTag}.*?</${specificTag}>`, 'g'), '');

    // Remove all whitespace
    let textLeft = withoutSpecific.replace(/\s/g, '');
    if (full) {
      return textLeft.length === 0;
    } else {
      // partial
      return textLeft.length > 0;
    }
  } catch (err) {
    console.error(err.message);
    return false;
  }
}

/**
 * Validates an entire event group against a style guide
 * @param {Object} eventGroup - The event group to validate
 * @param {Object} styleGuide - The style guide to validate against
 * @param {Object} options - Additional options
 * @param {number} options.frameRate - Frame rate for timecode calculations
 * @param {Object} options.sccLibrary - SCC character library
 * @param {Object} options.netflixLibrary - Netflix character library
 * @returns {Array} Array of error objects
 */
export function validateEventGroup(eventGroup, styleGuide, options = {}) {
  const errors = [];
  const {
    frameRate = 29.97,
    // Default frame rate
    dropFrame = true,
    excludeSpaces = false // When true, spaces are excluded from the CPS reading rate calculation
  } = options;
  if (!eventGroup || !eventGroup.events || !styleGuide) {
    return errors;
  }
  eventGroup.events.forEach((event, count, events) => {
    let messages = [];
    let error = {
      id: event.id,
      index: count + 1,
      text: convertToPlainText(event.text),
      messages: []
    };

    // Run all applicable tests based on the style guide
    messages.push(eventLineTest(event.text, styleGuide.maxLines));
    messages.push(eventCharTest(event.text, styleGuide.maxChars));
    messages.push(totalCharTest(event.text, styleGuide.totalMaxChars));
    messages.push(eventDurationTest(event.start, event.end, styleGuide.minDuration, styleGuide.maxDuration));
    messages.push(eventCpsTest(event.start, event.end, event.text, styleGuide.minCps, styleGuide.maxCps, excludeSpaces));
    messages.push(eventWpmTest(event.start, event.end, event.text, styleGuide.minWpm, styleGuide.maxWpm));
    if (styleGuide.illegalChars && sccLibrary) {
      messages.push(detectIllegalChars(event.text, sccLibrary.valid));
    }
    if (styleGuide.netflixGlyphs && netflixLibrary) {
      messages.push(detectIllegalChars(event.text, netflixLibrary.valid));
    }
    if (events[count + 1]) {
      messages.push(eventGapTest(event.end, events[count + 1].start, styleGuide.minEventGap, styleGuide.maxEventGap, styleGuide.minEventGapTolerance, styleGuide.maxEventGapTolerance, frameRate));
      if (styleGuide.overlap) {
        messages.push(overlapTest(event.end, events[count + 1].start));
      }
    }

    // Position Tests
    if (styleGuide.positionTopLeft) {
      messages.push(positionTest(event, 'start', 'start', 'top left'));
    }
    if (styleGuide.positionTopCenter) {
      messages.push(positionTest(event, 'start', 'center', 'top center'));
    }
    if (styleGuide.positionTopRight) {
      messages.push(positionTest(event, 'start', 'end', 'top right'));
    }
    if (styleGuide.positionCenterLeft) {
      messages.push(positionTest(event, 'center', 'start', 'center left'));
    }
    if (styleGuide.positionCenter) {
      messages.push(positionTest(event, 'center', 'center', 'center'));
    }
    if (styleGuide.positionCenterRight) {
      messages.push(positionTest(event, 'center', 'end', 'center right'));
    }
    if (styleGuide.positionBottomLeft) {
      messages.push(positionTest(event, 'end', 'start', 'bottom left'));
    }
    if (styleGuide.positionBottomCenter) {
      messages.push(positionTest(event, 'end', 'center', 'bottom center'));
    }
    if (styleGuide.positionBottomRight) {
      messages.push(positionTest(event, 'end', 'end', 'bottom rigth'));
    }
    if (styleGuide.positionYOffset) {
      messages.push(event.yOffset !== 0 ? 'vertical offset set' : undefined);
    }
    if (styleGuide.positionXOffset) {
      messages.push(event.xOffset !== 0 ? 'horizontal offset set' : undefined);
    }

    // Speaker Test
    if (styleGuide.missingSpeaker) {
      messages.push(event.speakers?.length > 0 && event.speakers[0]?.name ? undefined : 'Event is missing a speaker');
    }

    // Text format tests
    if (styleGuide.hyphenSpace) {
      messages.push(event.text.match(/-\s/) ? 'Text contains a hyphen followed by whitespace' : undefined);
    }
    if (styleGuide.hasUnderscore) {
      messages.push(event.text.match(/_/) ? 'Text contains an underscore' : undefined);
    }
    if (styleGuide.periods) {
      messages.push(event.text.match(/(?<!\.)\.{2}(?!\.)|\.{4,}/) ? 'Text contains 2 or 4+ dots (periods)' : undefined);
    }
    if (styleGuide.useEllipses) {
      messages.push(event.text.match(/\.{3,}/) ? 'Text is missing ellipses' : undefined);
    }
    if (styleGuide.spellNumbers) {
      messages.push(event.text.match(/(?<![0-9])([1-9]|10)(?![0-9])/) ? 'Numbers 1-10 should be written' : undefined);
    }
    if (styleGuide.spellNumbersAtStart) {
      messages.push(event.text.match(/>\s+[0-9]|>[0-9]/) ? 'Numbers at start of line should be written' : undefined);
    }

    // Repeat Words
    if (styleGuide.repeatWords) {
      messages.push(event.text.match(/\b(\w+)\s+\1\b/g) ? 'Text contains repeat words' : undefined);
    }

    // Fit Subtitles
    if (styleGuide.fitSubtitles) {
      messages.push(checkSubtitleFit(event.text, styleGuide.maxChars));
    }

    // Space and whitespace tests
    if (styleGuide.leadingTrailingSpace) {
      messages.push(checkLeadingSpaces(event.text));
    }
    if (styleGuide.whitespace) {
      messages.push(checkWhiteSpace(event.text));
    }
    if (styleGuide.blankLines) {
      messages.push(checkBlankLines(event.text));
    }

    // Format tests
    if (styleGuide.partialItalics) {
      messages.push(checkForFormatting(event.text, "em", false) ? 'Text contains partial italics' : undefined);
    }
    if (styleGuide.partialBold) {
      messages.push(checkForFormatting(event.text, "strong", false) ? 'Text contains partial bold' : undefined);
    }
    if (styleGuide.partialUnderline) {
      messages.push(checkForFormatting(event.text, "u", false) ? 'Text contains partial underline' : undefined);
    }
    if (styleGuide.fullItalics) {
      messages.push(checkForFormatting(event.text, "em", true) ? 'Text contains italics' : undefined);
    }
    if (styleGuide.fullBold) {
      messages.push(checkForFormatting(event.text, "strong", true) ? 'Text contains bolded elements' : undefined);
    }
    if (styleGuide.fullUnderline) {
      messages.push(checkForFormatting(event.text, "u", true) ? 'Text contains underlined elements' : undefined);
    }

    // Approval tests
    if (styleGuide.approvalPassed) {
      messages.push(event.approved === true ? 'Event PASSED approved' : undefined);
    }
    if (styleGuide.approvalFailed) {
      messages.push(event.approved === false ? 'Event FAILED approval' : undefined);
    }
    if (styleGuide.approvalNotSet) {
      messages.push(event.approved === undefined ? 'Event approval NOT SET' : undefined);
    }

    // Notes and reply tests
    if (styleGuide.notes) {
      messages.push(event.notes?.trim() ? 'Event contains notes' : undefined);
    }
    if (styleGuide.reply) {
      messages.push(event.reply?.trim() ? 'Event contains replies' : undefined);
    }
    if (styleGuide.notesDone) {
      messages.push(event.notes?.trim() && !event.notesCheck ? 'Event contains notes that are incomplete' : undefined);
    }
    if (styleGuide.replyDone) {
      messages.push(event.reply?.trim() && !event.replyCheck ? 'Event contains replies that are incomplete' : undefined);
    }

    // Tags and forced subtitles
    if (styleGuide.tags) {
      messages.push(event.tags?.length > 0 ? 'Event contains tags' : undefined);
    }
    if (styleGuide.forced) {
      messages.push(event.forced ? 'Forced Subtitle Event' : undefined);
    }
    error.messages = messages.filter(message => message);
    if (error.messages.length > 0) {
      errors.push(error);
    }
  });
  return errors;
}